import React from 'react';
import * as Yup from 'yup';
import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  makeStyles,
  Theme,
  Button,
} from '@material-ui/core';
import { Callout } from '../components/Callout';
import { FormBodyAPI, ModuleTypeValue, URLValidationRegex } from '../constants';
import * as Colors from '../theme/colors';
import { renameDisabledModules } from '../constants/moduleManagementConsts';
import { DescriptionTooltip } from '../components/Tooltip';
import BaseTypography from '../components/Text/BaseTypography';
import { BaseTextField, ResizableTextField } from '../components/TextField';
import { IconPicker } from '../components/Settings/ExtensionsPage/IconPicker';
import { createEmbedBody } from '../utils/ExtensionUtils';
import { SettingsClient } from '../clients/SettingsClient';
import { EditModulePageQueryParams } from '../components/Settings/ModuleManagementPage/SetupModule/useEditingModule';
import { ExtensionModuleContentPreview } from '../components/Settings/ModuleManagementPage/ExtensionModuleContentPreview/ExtensionModuleContentPreview';
import { RouteContext } from '../context';
import {
  ExtensionViewType,
  ExtensionViewTypeValue,
} from '../components/Settings/ExtensionsPage/ExtensionViewType';
import { useExtensionFormFieldsHandlers } from './useExtensionFormFieldsHandlers';
import { typography13RegularStyle } from '../components/Text';
import { ModuleTypes } from '../components/Settings/ModuleManagementPage/SetupModule/moduleManagementTypes';
import { ExtensionEmbedFieldLabel } from '../components/ExtensionPage/ExtensionEmbedFieldLabel';
import { docsURL } from '../constants/linkConsts';

const useStyles = makeStyles((theme: Theme) => ({
  instructionTextWrapper: {
    marginBottom: theme.spacing(3),
  },
  iconPickerWrapper: {
    marginTop: theme.spacing(3.5),
  },
  extensionTypeSelectWrapper: {
    marginTop: theme.spacing(2.5),
  },
  embedInputWrapper: {
    marginTop: theme.spacing(4),
  },
  flexContainer: {
    display: 'flex',
  },
  helperText: {
    color: Colors.GraySmall,
  },
  extensionViewTypeWrapper: {
    marginTop: theme.spacing(3.5),
  },
  tooltipText: {
    marginTop: theme.spacing(3.5),
  },
  linkButton: {
    ...typography13RegularStyle,
    minWidth: 'auto',
    verticalAlign: 'baseline',
    color: Colors.WebLink,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  flexAlignCenter: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const getSubmitValues = async () => ({});

export const useEditModuleForm = () => {
  const { query } = React.useContext(RouteContext);
  const { moduleType, preset = '' } = query as EditModulePageQueryParams;
  const extensionFieldsValidationV2 = {
    type: Yup.string(),
    autoSizeEmbed: Yup.boolean().when('type', {
      is: 'global',
      then: Yup.boolean().required(),
    }),
    content: Yup.string()
      .trim()
      .nullable()
      .when('type', {
        is: type => type !== ModuleTypes.Local,
        then: Yup.string().required('Invalid embed content'),
      }),
    // when view type is embed, embedLink is required
    embedCode: Yup.string()
      .trim()
      .nullable()
      // when editing a non-local extension with embed view type, embedCode is required
      .when(['type', 'viewType'], {
        is: (type, viewType) =>
          type !== ModuleTypes.Local &&
          viewType === ExtensionViewTypeValue.EMBED,
        then: Yup.string().required('Embed code cannot be empty'),
      })
      .when('type', {
        is: type => type === ModuleTypes.Authenticated,
        then: Yup.string()
          .required('Authenticated extension requires a url')
          .matches(URLValidationRegex, 'Invalid URL.'),
      }),
    // when view type is link, embedLink is required
    embedLink: Yup.string()
      .trim()
      .nullable()
      // when editing a non-local extension with embed view type, embedLink is required
      .when(['type', 'viewType'], {
        is: (type, viewType) =>
          type !== ModuleTypes.Local &&
          viewType === ExtensionViewTypeValue.LINK,
        then: Yup.string()
          .required('Embed link cannot be empty')
          .matches(URLValidationRegex, 'Invalid URL.'),
      }),
    viewType: Yup.string().required('View type is required'),
  };

  const validationScheme = Yup.object().shape({
    id: Yup.string(), // used only to check if the module if rename-able
    label: Yup.string().required('App name is required.'),
    icon: Yup.string().required('App icon is required.'),
    // add extension form validation scheme
    ...(moduleType !== 'module' ? extensionFieldsValidationV2 : {}),
  });

  const [_, setValidatingEmbedCode] = React.useState(false);
  const classes = useStyles();

  const FormRenderer: React.FC<FormBodyAPI> = ({
    values,
    errors,
    handleChange,
    handleBlur,
    setFieldValue,
    touched,
  }) => {
    /**
     * when embed code changes check if it is url
     * if it is url then validate if returns embedly data
     * and use that for rendering
     */
    const validateEmbedCode = async (
      event: React.ChangeEvent<HTMLTextAreaElement>,
    ) => {
      const embedCode = event.target.value;
      setFieldValue('embedCode', embedCode);
      setFieldValue('embedlyData', null);

      const urlRegex = new RegExp(URLValidationRegex);
      if (!embedCode.match(urlRegex)) {
        // if embedCode is not url dont validate embedCode
        return;
      }

      setValidatingEmbedCode(true);
      try {
        const result = await SettingsClient.validateExtension(embedCode);
        setFieldValue('embedlyData', result);
      } catch (ex) {
        setFieldValue('embedlyData', null);
      }
      setValidatingEmbedCode(false);
    };

    const {
      handleEmbedContentInputChange,
      handleViewTypeChanged,
      contentFieldHelperText,
    } = useExtensionFormFieldsHandlers(
      setFieldValue,
      handleChange,
      values,
      errors,
      touched,
    );

    const renderModuleNameInput = (options?: { disabled: boolean }) => (
      <BaseTextField
        autoFocus
        fullWidth
        label={
          <div className={classes.flexAlignCenter}>
            <BaseTypography fontType="13Medium">Name</BaseTypography>
            <BaseTypography className={classes.helperText}>
              &nbsp;- How it should appear in the sidebar
            </BaseTypography>
          </div>
        }
        variant="outlined"
        sizeVariant="medium"
        name="label"
        value={values.label}
        onChange={handleChange}
        onBlur={handleBlur}
        error={Boolean(touched && touched.label && errors && errors.label)}
        helperText={touched && touched.label && errors && errors.label}
        {...(options || {})}
      />
    );

    return (
      <>
        {renameDisabledModules.includes(values.id) ? (
          <DescriptionTooltip
            placement="bottom"
            title={
              <BaseTypography fontType="13Medium">
                Editing the name is disabled for this module.
              </BaseTypography>
            }
          >
            <div>{renderModuleNameInput({ disabled: true })}</div>
          </DescriptionTooltip>
        ) : (
          renderModuleNameInput()
        )}

        <div className={classes.iconPickerWrapper}>
          <BaseTypography fontType="13Medium">Sidebar icon</BaseTypography>
          <IconPicker
            icon={values.icon || 'dashboard'}
            onIconSelected={iconName => setFieldValue('icon', iconName)}
          />
        </div>
        {moduleType === 'extension' && (
          <div className={classes.extensionTypeSelectWrapper}>
            <BaseTextField
              sizeVariant="medium"
              select
              fullWidth
              variant="outlined"
              onChange={handleChange}
              label="Setup type"
              onBlur={handleBlur}
              name="type"
              defaultValue="global"
              value={values.type}
              disabled={Boolean(values.id)} // disabled when editing an existing module
            >
              <MenuItem value="global">
                Automatic — All clients see the same content
              </MenuItem>
              <MenuItem value="local">
                Manual — Manually connect content for each client
              </MenuItem>

              {/* preset custom extensions should cannot be authenticated since they are not customizable by customers */}
              {!preset && (
                <MenuItem value="authenticated">
                  Custom (requires API) - Client sees content based on the
                  embedded web application
                </MenuItem>
              )}
            </BaseTextField>
            {values.type === ModuleTypeValue.LOCAL_EXTENSION && (
              <div className={classes.tooltipText}>
                <Callout>
                  {`After you create this app, navigate back to your portal and click on "Partner & Custom Apps" in the sidebar. Then manually connect content for each client.`}
                </Callout>
              </div>
            )}
          </div>
        )}

        {values.type === ModuleTypeValue.GLOBAL_EXTENSION && (
          <div className={classes.extensionViewTypeWrapper}>
            <BaseTypography fontType="13Medium">View type</BaseTypography>
            <ExtensionViewType
              onChange={handleViewTypeChanged}
              onBlur={handleBlur}
              value={values.viewType}
              fieldName="viewType"
            />
          </div>
        )}

        {/* Global extension inputs */}
        {(values.type === ModuleTypes.Global ||
          values.type === ModuleTypes.Authenticated) && (
          <>
            <div className={classes.embedInputWrapper}>
              <ResizableTextField
                fullWidth
                error={Boolean(contentFieldHelperText())}
                helperText={contentFieldHelperText() || ' '}
                variant="outlined"
                name="content"
                onChange={handleEmbedContentInputChange}
                value={values.content}
                onBlur={handleBlur}
                label={
                  values.type === ModuleTypes.Authenticated ? (
                    <BaseTypography fontType="13Medium">
                      Custom App URL
                    </BaseTypography>
                  ) : (
                    <ExtensionEmbedFieldLabel
                      extensionViewType={values.viewType}
                    />
                  )
                }
              />
            </div>
            {/* Render render auto-size and preview when extension view type is embed */}
            {values.viewType === ExtensionViewTypeValue.EMBED && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.autoSizeEmbed}
                    onChange={handleChange}
                    name="autoSizeEmbed"
                    color="primary"
                  />
                }
                label="Auto size embed"
                id="autoSize-input"
              />
            )}

            {/* Render extension preview when view type is embed and it is an not authenticated extension */}
            {values.viewType === ExtensionViewTypeValue.EMBED &&
              values.type !== ModuleTypes.Authenticated && (
                <ExtensionModuleContentPreview
                  extensionIcon={values.icon}
                  extensionName={values.label}
                  embedContent={
                    values.embedlyData?.html || values.embedCode
                      ? createEmbedBody({
                          embedCode:
                            values.embedlyData?.html || values.embedCode,
                          autoSize: values.autoSizeEmbed,
                        })
                      : ''
                  }
                />
              )}
            {values.type === ModuleTypes.Authenticated && (
              <div className={classes.tooltipText}>
                <Callout>
                  <BaseTypography fontType="13Regular">
                    A client or company ID is passed to the embedded application
                    at the provided app URL. The application determines the
                    content that is shown using the client or company ID. You
                    can learn more about custom apps{' '}
                    <Button
                      className={classes.linkButton}
                      href={`${docsURL}/page/custom-apps`}
                      target="_blank"
                    >
                      here
                    </Button>
                    .
                  </BaseTypography>
                </Callout>
              </div>
            )}
          </>
        )}
      </>
    );
  };

  return {
    FormRenderer,
    validationScheme,
    getSubmitValues,
  };
};
